(() => {
  const BRIDGE_KEY = 'egifts-g-promo-bridge-v2';
  const MAX_WAIT_MS = 8000;
  const POLL_MS = 200;
  const FALLBACK_RELOAD_DELAY = 120;

  if (window.EgiftsGPromoBridgeV2) return;
  window.EgiftsGPromoBridgeV2 = true;

  const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const getProperties = (item) => {
    return item && typeof item.properties === 'object' && item.properties ? item.properties : {};
  };

  const isSameDesignAddon = (item) => {
    return String(getProperties(item)._egifts_same_design || '') === '1';
  };

  const needsPromoSync = (item) => {
    const properties = getProperties(item);

    return (
      isSameDesignAddon(item) &&
      !String(properties.__mpb_offer_id || '')
    );
  };

  const waitForG = async () => {
    const started = Date.now();

    while (Date.now() - started < MAX_WAIT_MS) {
      const mpb = window.mpb;

      if (
        mpb &&
        typeof mpb.fetch_cart === 'function' &&
        typeof mpb.fetch_product_by_id === 'function' &&
        typeof mpb.get_valid_product_offer === 'function' &&
        typeof mpb.change_cart_line === 'function'
      ) {
        return mpb;
      }

      await sleep(POLL_MS);
    }

    return null;
  };

  const buildGuardKey = (cart) => {
    const addonKeys = (cart.items || [])
      .filter(needsPromoSync)
      .map((item) => item.key)
      .sort()
      .join('|');

    return `${BRIDGE_KEY}:${cart.token || 'cart'}:${addonKeys}`;
  };

  const hasFreshGuard = (guardKey) => {
    try {
      const value = Number(sessionStorage.getItem(guardKey) || 0);
      return Boolean(value && Date.now() - value < 15000);
    } catch (error) {
      return false;
    }
  };

  const setGuard = (guardKey) => {
    try {
      sessionStorage.setItem(guardKey, String(Date.now()));
    } catch (error) {}
  };

  const clearGuard = (guardKey) => {
    try {
      sessionStorage.removeItem(guardKey);
    } catch (error) {}
  };

  const syncAddon = async (mpb, item) => {
    const product = await mpb.fetch_product_by_id(item.product_id);
    if (!product) return false;

    const offer = await mpb.get_valid_product_offer(product);
    if (!offer?.uuid) return false;

    const properties = getProperties(item);

    const response = await mpb.change_cart_line({
      id: item.key,
      quantity: item.quantity,
      properties: {
        ...properties,
        __mpb_offer_id: String(offer.uuid)
      }
    });

    return Boolean(response);
  };

  const parseSection = (html, selector) => {
    if (!html || !selector) return null;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(selector);
  };

  const replaceSectionContents = (sectionHtml, currentSelector, freshSelector = currentSelector) => {
    const current = document.querySelector(currentSelector);
    const fresh = parseSection(sectionHtml, freshSelector);

    if (!current || !fresh) return false;

    current.innerHTML = fresh.innerHTML;
    return true;
  };

  const refreshCartSections = async () => {
    const mainItems = document.getElementById('main-cart-items');
    if (!mainItems?.dataset?.id) return false;

    const sectionIds = [mainItems.dataset.id];

    const mainFooter = document.getElementById('main-cart-footer');
    if (mainFooter?.dataset?.id) {
      sectionIds.push(mainFooter.dataset.id);
    }

    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (cartIconBubble) {
      sectionIds.push('cart-icon-bubble');
    }

    const uniqueSectionIds = [...new Set(sectionIds)].slice(0, 5);
    const url = new URL(window.location.href);
    url.searchParams.set('sections', uniqueSectionIds.join(','));

    const response = await window.fetch(url.toString(), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      cache: 'no-store',
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error(`Section Rendering HTTP ${response.status}`);
    }

    const sections = await response.json();

    const mainItemsHtml = sections[mainItems.dataset.id];
    const itemsUpdated = replaceSectionContents(
      mainItemsHtml,
      '#main-cart-items .js-contents',
      '#main-cart-items .js-contents'
    );

    if (!itemsUpdated) {
      throw new Error('Nie udało się odświeżyć main-cart-items.');
    }

    if (mainFooter?.dataset?.id && sections[mainFooter.dataset.id]) {
      replaceSectionContents(
        sections[mainFooter.dataset.id],
        '#main-cart-footer .js-contents',
        '#main-cart-footer .js-contents'
      );
    }

    if (cartIconBubble && sections['cart-icon-bubble']) {
      replaceSectionContents(
        sections['cart-icon-bubble'],
        '#cart-icon-bubble',
        '#cart-icon-bubble'
      );
    }

    if (window.EgiftsSameDesignCartV8?.initAll) {
      window.EgiftsSameDesignCartV8.initAll(document);
    }

    document.dispatchEvent(
      new CustomEvent('egifts:g-promo-synced', {
        bubbles: true
      })
    );

    return true;
  };

  const fallbackReload = () => {
    window.setTimeout(() => {
      window.location.reload();
    }, FALLBACK_RELOAD_DELAY);
  };

  const run = async () => {
    if (!window.location.pathname.endsWith('/cart')) return;

    const mpb = await waitForG();
    if (!mpb) return;

    let cart;

    try {
      cart = await mpb.fetch_cart();
    } catch (error) {
      return;
    }

    const targets = (cart.items || []).filter(needsPromoSync);
    if (!targets.length) return;

    const guardKey = buildGuardKey(cart);
    if (hasFreshGuard(guardKey)) return;

    setGuard(guardKey);

    let changed = false;

    try {
      for (const item of targets) {
        const synced = await syncAddon(mpb, item);
        if (synced) changed = true;
      }

      if (!changed) {
        clearGuard(guardKey);
        return;
      }

      const freshCart = await mpb.fetch_cart();
      const stillMissing = (freshCart.items || []).some(needsPromoSync);

      if (stillMissing) {
        clearGuard(guardKey);
        return;
      }

      try {
        const refreshed = await refreshCartSections();

        if (!refreshed) {
          fallbackReload();
        }
      } catch (error) {
        console.warn(
          'E-gifts: odświeżenie sekcji koszyka nie powiodło się, używam jednego reloadu.',
          error
        );
        fallbackReload();
      }
    } catch (error) {
      clearGuard(guardKey);
      console.warn(
        'E-gifts: nie udało się zsynchronizować promocji G.',
        error
      );
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();